import {
  Breadcrumb,
  Button,
  Dropdown,
  message,
  Popconfirm,
  Spin,
  Tag,
} from "antd";
import React, { useState } from "react";
import Table, { ColumnType } from "antd/es/table";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { CloseOutlined, SlidersOutlined } from "@ant-design/icons";
import { ConsultationFilterMenu } from "./ConsultationFilter";
import {
  useConsultationList,
  useUpdateConsultationStatusServiceUpdate,
} from "../../../hooks/api/consultation-bookings/hooks";
import { cn } from "../../../lib/utils";
import MyPagination from "../../components/Pagination";
import { IConsultationBookingPayloadType } from "../../../types/api/consultation-book-types";

const ConsultationTable = () => {
  const navigate = useNavigate();
  const [breadcrumb] = useState("Consultations");
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const {
    data,
    refetchWithQuery: refetch,
    isFetching,
  } = useConsultationList({
    page: 1,
    limit: 10,
  });

  const { mutate: updateStatus } = useUpdateConsultationStatusServiceUpdate({
    onSuccess: () => {
      setUpdatingId(null);
      message.success("Status updated successfully");
    },
    onError: (error: any) => {
      setUpdatingId(null);
      message.error(error?.message);
    },
  });

  /** ---- 🎯 Store filters as single-value only ---- **/
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string>
  >({});

  /** ---- Remove selected filter ---- **/
  const removeFilter = (key: string) => {
    setSelectedFilters((prev) => {
      const updated = { ...prev };
      delete updated[key];


      refetch({ page: 1, limit: 10, ...updated });

      return updated;
    });
  };
  const DEFAULT_NUQS_CONFIG = {
    throttleMs: Infinity,
  };

  const qData = data?.qData ?? { page: 1, limit: 10 };

  const columns: ColumnType<IConsultationBookingPayloadType>[] = [
    {
      title: "Consultation ID",
      dataIndex: "booking_id",
      render: (id: string) => <span className="text-gray-800">{id}</span>,
    },
    {
      title: "User",
      dataIndex: "name",
      render: (name: string) => (
        <span className="text-gray-800">{name || "-"}</span>
      ),
    },
    {
      title: "Date & Time",
      dataIndex: "date",
      render: (_, row) => (
        <span className="text-gray-800">
          {moment(row.date).format("D MMM, YYYY")} •{" "}
          {moment(row.start_time).format("hh:mm A")}
        </span>
      ),
    },
    {
      title: "Purpose",
      dataIndex: "purpose",
      render: (purpose: string) => (
        <span className="text-gray-800">{purpose || "-"}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (_, row) => {
        const status = row.status ?? "UPCOMING";

        const colorMap: Record<string, string> = {
          UPCOMING: "bg-blue-100 text-blue-700",
          APPROVED: "bg-green-100 text-green-700",
          CANCELLED: "bg-red-200 text-red-700",
          COMPLETE: "bg-green-200 text-green-700",
        };

        return updatingId === row._id ? (
          <Spin size="small" />
        ) : (
          <Tag
            className={cn(
              "px-3 py-1 rounded-full text-xs font-semibold capitalize",
              colorMap[status]
            )}
          >
            {status.replace(/_/g, " ")}
          </Tag>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      align: "right",
      render: (_, service) => (
        <div className="flex items-center justify-end gap-3">
          <span
            onClick={() => navigate(`/consultation/details/${service._id}`)}
            className="cursor-pointer text-primary"
          >
            View
          </span>

          {service.status !== "CANCELLED" && (
            <Popconfirm
              title="Cancel this consultation?"
              okText="Yes"
              cancelText="No"
              onConfirm={() => {
                setUpdatingId(service._id ?? "");
                updateStatus({ _id: service._id, status: "CANCELLED" });
              }}
            >
              <span className="cursor-pointer text-red-500">Cancel</span>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <h1 className="text-lg sm:text-3xl font-semibold">Consultations</h1>

      <Breadcrumb
        separator=">"
        items={[{ title: "Dashboard" }, { title: breadcrumb }]}
        className="text-xs sm:text-base my-2"
      />

      {/* Filter Button */}
      <div className="flex justify-end mb-3">
        <Dropdown
          trigger={["click"]}
          placement="bottomRight"
          dropdownRender={() => (
            <ConsultationFilterMenu
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              refetch={refetch}
            />
          )}
        >
          <Button
            icon={<SlidersOutlined />}
            className="bg-[#FEF2E7] custom_shadow font-semibold"
          >
            Filters
          </Button>
        </Dropdown>
      </div>

      {/* Selected Filter Tags */}
      {Object.keys(selectedFilters).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(selectedFilters).map(([key, value]) => (
            <Tag
              key={key}
              closable
              color="green"
              onClose={() => removeFilter(key)}
              closeIcon={<CloseOutlined />}
            >
              {value}
            </Tag>
          ))}
        </div>
      )}

      {/* Table */}
      <Table
        dataSource={data?.data}
        columns={columns}
        rowKey={(item) => item._id ?? ""}
        loading={isFetching}
        pagination={false}
        scroll={{ x: true }}
        size="middle"
        className="custom-rounded-table"
        footer={() => (
          <MyPagination
            qData={qData}
            onChange={refetch}
            total={qData.total}
            DEFAULT_NUQS_CONFIG={DEFAULT_NUQS_CONFIG}
          />
        )}
      />
    </div>
  );
};

export default ConsultationTable;
